// React hooks
import { useState } from 'react';
// Redux for state management
import { useDispatch } from 'react-redux';
// Icons
import { AlertCircle, Calendar, Heart, ArrowRight, ArrowLeft } from 'lucide-react';
// Redux actions
import { setProfile, setPage } from '../../Redux/store';
// Image upload component
import ImageArrayEditorContainer from '../../components/ImageArrayEditorContainer';
// Location input with GPS
import LocationSelector from '../../components/LocationSelector';
// Auto-saving input component
import InputTimed from '../../components/InputTimed';
// Database client
import { supabase } from '../../supabase/client';
// Profile update helper
import { updateProfileField } from '../../data/updateProfileField';

// Stats to display
const STAT_KEYS = ['age', 'height', 'method'];

// Edit mode for own profile
function ProfileEditor({ user, onSave, onCancel }) {
  const dispatch = useDispatch();
  const [error, setError] = useState(null);

  // Extract for convenience
  const stats = user?.stats ?? {};
  const images = user?.images ?? [];
  const firstTimeViews = user?.first_time_views || {};
  const isFirstTime = !firstTimeViews.profile_welcome;

  // Validate required fields
  const handleCompleteSetup = async (navigateTo = null) => {
    const missing = [];
    if (!user?.name) missing.push('name');
    if (!user?.location) missing.push('location');
    if (!user?.age) missing.push('age');
    if (!images || images.length === 0) missing.push('photo');

    // Show error if missing fields
    if (missing.length > 0) {
      setError(`Please check ${missing.join(', ')} field`+(missing.length>1?"s":""));
      return;
    }

    // Clear error before saving
    setError(null);

    try {
      // Generate unique share code
      const baseCode = user.name.toLowerCase().trim().replace(/\s+/g, '.');
      const { data: existing } = await supabase
        .from('profiles')
        .select('share_code')
        .like('share_code', `${baseCode}%`);

      let finalCode = baseCode;
      if (existing && existing.length > 0) {
        const codes = existing.map(e => e.share_code);
        let n = 2;
        while (codes.includes(`${baseCode}.${n}`)) {
          n++;
        }
        if (codes.includes(baseCode)) {
          finalCode = `${baseCode}.${n}`;
        }
      }

      const updateData = {
        share_code: finalCode,
        first_time_views: { ...firstTimeViews, profile_welcome: true }
      };

      // Update DB and Redux in parallel
      const [dbRes] = await Promise.all([
        supabase.from('profiles').update(updateData).eq('id', user.id),
        dispatch(setProfile({ ...user, ...updateData }))
      ]);

      if (dbRes.error) throw dbRes.error;

      // Navigate to specified page
      if (navigateTo) {
        dispatch(setPage(navigateTo));
      }
    } catch (err) {
      setError('Failed to complete setup. Please try again.');
    }
  };

  // Route save based on first-time status
  const handleSave = () => {
    if (isFirstTime) {
      handleCompleteSetup();
    } else {
      onSave();
    }
  };

  return (
    <div className="relative h-full overflow-y-auto">
      {/* Error toast */}
      {error && (
        <div className="fixed bottom-20 left-4 right-4 z-[100] animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="flex items-center gap-2 rounded-xl bg-background/80 border border-yellow-400 px-3 py-2 text-xs font-normal text-foreground shadow backdrop-blur-sm">
            <span className="text-yellow-400">🙂</span>
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="shrink-0 rounded-full p-1 hover:bg-yellow-100">
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* Image editor */}
      <ImageArrayEditorContainer
        userId={user?.id}
        images={images}
        editable={true}
        shorter={true}
      />

      <div className="space-y-4 px-5 pt-5 pb-24">
        {/* Name and age inputs */}
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-primary">Name</label>
            <InputTimed
              type="text"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
              defaultValue={user?.name}
              onCommit={(value) => updateProfileField(user, 'name', value, dispatch)}
            />
          </div>
          <div className="w-20 shrink-0">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-primary">Age</label>
            <InputTimed
              type="number"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground"
              defaultValue={user?.age || ''}
              onCommit={(value) => updateProfileField(user, 'age', value, dispatch)}
            />
          </div>
        </div>

        {/* Location input */}
        <LocationSelector location={user?.location} editable={true} />

        {/* Role selector - only shown on first-time setup */}
        {isFirstTime && (
          <div className="rounded-xl border border-border p-4 space-y-2">
            <label className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground ml-1 block mb-1.5">My Role</label>
            <select
              value={user?.role || 'ip'}
              onChange={(e) => updateProfileField(user, 'role', e.target.value, dispatch)}
              className="w-full rounded-xl border border-border bg-background px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
            >
              <option value="ip">Intended Parent</option>
              <option value="donor">Donor/ Viewer</option>
            </select>
          </div>
        )}

        {/* Bio input */}
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary">About</h3>
          <InputTimed
            type="textarea"
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground min-h-[80px] resize-y"
            defaultValue={user?.bio}
            onCommit={(value) => updateProfileField(user, 'bio', value, dispatch)}
          />
        </div>

        {/* Stats display */}
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Stats</h3>
          <div className="grid grid-cols-2 gap-2">
            {STAT_KEYS.filter((k) => stats[k]).map((k) => (
              <div key={k} className="rounded-lg bg-secondary px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
                <div className="text-sm font-medium text-secondary-foreground">{stats[k]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Save buttons */}
        {isFirstTime ? (
          <div className="fixed bottom-2.5 left-4 right-4 z-30 flex justify-center">
            {/* Do not delete, keep commented out */}
            {/* <button
              onClick={() => handleCompleteSetup('match')}
              className="flex items-center justify-center gap-2 rounded-full bg-primary/80 px-5 py-3 text-sm font-medium text-primary-foreground shadow-lg backdrop-blur-sm"
            >
              <Heart className="h-5 w-5" />
              <ArrowLeft className="h-4 w-4" />
            </button> */}
            <button
              onClick={() => handleCompleteSetup('calendar')}
              className="flex items-center justify-center gap-2 rounded-full bg-secondary/80 px-5 py-3 text-sm font-medium text-secondary-foreground shadow-lg backdrop-blur-sm"
            >
              Go To Calendar
              <ArrowRight className="h-4 w-4" />
              <Calendar className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="fixed bottom-2.5 right-4 z-30">
            <button
              onClick={handleSave}
              className="flex items-center gap-2 rounded-full bg-primary/80 px-5 py-3 text-sm font-medium text-primary-foreground shadow-lg backdrop-blur-sm"
            >
              <Check className="h-4 w-4" />
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Check icon
function Check(props) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M20 6 9 17l-5-5"/></svg>;
}

// Close icon
function X(props) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>;
}

export default ProfileEditor;
